/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import { Flex } from '@getgo/chameleon-chakra-ui';
import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { SessionContext } from '../../../../../context/SessionContext';
import { isInstantJoinOfferedAndSupported } from '../../../../../services/MeetingService';
import {
  TelemetryAction,
  TelemetryComponent,
  TelemetryState,
  useSendTelemetry,
} from '../../../../../hooks/useSendTelemetry';
import {
  buildInstantJoinUrl,
  buildReInstallEPUrl,
} from '../../../../../services/UrlService';
import { Spacing } from '../../../../../themes/GoToTheme';
import { GoToLink } from '../Texts/GoToLink';
import { GoToText } from '../../../../Text/GoToText';

interface Props {
  onTryAgain: () => void;
  telemetryState: TelemetryState;
}

const messages = defineMessages({
  caption: {
    id: 'goto.join.troubleshooting.caption',
  },
  tryAgain: {
    id: 'goto.join.troubleshooting.tryAgain',
  },
  joinInBrowser: {
    id: 'goto.join.troubleshooting.joinInBrowser',
  },
  redownload: {
    id: 'goto.join.troubleshooting.redownload',
  },
});

export const GoToNotStarting: React.FC<Props> = ({
  onTryAgain,
  telemetryState,
}) => {
  const { formatMessage: f } = useIntl();
  const { meetingId, sessionTrackingId, meetingInfo, webAppUrl } =
    useContext(SessionContext);
  const redirectLinkForReinstallationOfEP = buildReInstallEPUrl(
    meetingId,
    sessionTrackingId
  );
  const instantJoinUrl =
    webAppUrl || buildInstantJoinUrl(meetingId, sessionTrackingId);
  const isWebSupported = isInstantJoinOfferedAndSupported(meetingInfo);
  const sendTelemetry = useSendTelemetry();
  return (
    <>
      <GoToText marginTop={Spacing.CoreLayout3}>{f(messages.caption)}</GoToText>
      <Flex>
        <GoToLink
          onClick={() => {
            onTryAgain();
            sendTelemetry(
              TelemetryComponent.Native,
              TelemetryAction.Retry,
              telemetryState
            );
          }}
        >
          {f(messages.tryAgain)}
        </GoToLink>
        <GoToText marginX={Spacing.XSmall}>|</GoToText>
        <GoToLink
          href={
            isWebSupported ? instantJoinUrl : redirectLinkForReinstallationOfEP
          }
          onClick={() =>
            sendTelemetry(
              isWebSupported
                ? TelemetryComponent.Web
                : TelemetryComponent.Native,
              isWebSupported ? TelemetryAction.Join : TelemetryAction.Install,
              telemetryState
            )
          }
        >
          {f(isWebSupported ? messages.joinInBrowser : messages.redownload)}
        </GoToLink>
      </Flex>
    </>
  );
};
